import React, { createContext, useContext, useEffect, useState } from 'react';
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { BarChart3, ExternalLink, FolderTree, LayoutGrid, Loader2, LogOut, MessageCircle, Moon, Newspaper, Package, Sun, Users } from 'lucide-react';
import { useAuth } from '../../../contexts/AuthContext';
import { getAuthToken } from '../../../lib/api';
import { API_ORIGIN } from '../../../lib/apiBase';
import { prefetchStyles } from '../../../lib/catalogueAdminApi';
import { requestDesktopNotifications, useWaitingChatAlerts } from '../livechat/useWaitingChatAlerts';
import { useAdminMode, useIndicator } from '../theme/hooks';
import '../theme/admin-theme.css';
import { cn } from '../../../lib/utils';
import { Toaster } from '../../ui/sonner';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../ui/dropdown-menu';

type NavItem = { label: string; to: string; icon: React.ComponentType<{ className?: string }>; adminOnly?: boolean; end?: boolean };

const NAV: NavItem[] = [
  { label: 'Dashboard', to: '/admin', icon: LayoutGrid, end: true },
  { label: 'Analytics', to: '/admin/analytics', icon: BarChart3 },
  { label: 'Catalogue', to: '/admin/catalogue', icon: Package },
  { label: 'Blog', to: '/admin/blog', icon: Newspaper },
  { label: 'Live chat', to: '/admin/livechat', icon: MessageCircle },
];

const initialsOf = (name: string) =>
  name
    .split(/[\s._@-]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() ?? '')
    .join('');

// Live chat availability: read on mount, toggled from the header.
const useLiveChatOnline = () => {
  const [online, setOnline] = useState<boolean | null>(null);
  useEffect(() => {
    fetch(`${API_ORIGIN}/.netlify/functions/livechat/status`)
      .then((r) => r.json())
      .then((d) => d.success && setOnline(!!d.isOnline))
      .catch(() => setOnline(null));
  }, []);
  const toggle = async () => {
    if (online === null) return;
    const next = !online;
    setOnline(next);
    // Going online is a click, so the browser allows the notification prompt here.
    if (next) requestDesktopNotifications();
    try {
      const res = await fetch(`${API_ORIGIN}/.netlify/functions/livechat/admin/online`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getAuthToken()}` },
        body: JSON.stringify({ isOnline: next }),
      });
      if (!(await res.json()).success) setOnline(!next);
    } catch {
      setOnline(!next);
    }
  };
  return { online, toggle };
};

type ShellCtx = { actionsEl: HTMLElement | null; chatOnline: boolean | null };
const ShellContext = createContext<ShellCtx>({ actionsEl: null, chatOnline: null });
/** Header slot for page actions (the dashboard's date range) and shared shell state. */
export const useAdminShell = () => useContext(ShellContext);

const OcMark: React.FC = () => (
  <span className="grid h-[26px] w-[26px] place-items-center rounded-[7px] bg-[var(--oa-accent)] font-montserrat text-[10.5px] font-extrabold tracking-[-0.04em] text-[var(--oa-on-accent)]">
    OC
  </span>
);

// Admin chrome: a glass header over a drifting backdrop (ported from the Splitfin
// Manager prototype, docs/design-system/splitfin-handoff.md), a phone tab bar, and
// the auth gate. Pages render into <main>.
const AdminShell: React.FC = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { isAuthenticated, isLoading, user, isAdmin, signOut } = useAuth();
  const chat = useLiveChatOnline();
  const waitingChats = useWaitingChatAlerts(isAuthenticated && chat.online === true, pathname.startsWith('/admin/livechat'));
  const path = pathname.replace(/\/+$/, '') || '/admin';
  // The dashboard and analytics scroll as pages of cards; every other page is a full-height tool in one framed panel.
  const framed = path !== '/admin' && path !== '/admin/analytics';
  const { mode, toggle: toggleMode } = useAdminMode();
  const [actionsEl, setActionsEl] = useState<HTMLElement | null>(null);
  const [scrolled, setScrolled] = useState(false);

  const items = NAV.filter((n) => !n.adminOnly || isAdmin);
  const isActive = (n: NavItem) => (n.end ? path === n.to : path.startsWith(n.to));
  const activeIdx = items.findIndex(isActive);
  const nav = useIndicator<HTMLElement>('a[aria-current="page"]', [activeIdx, isAuthenticated]);
  const tabs = useIndicator<HTMLElement>('a[aria-current="page"]', [activeIdx, isAuthenticated]);

  useEffect(() => {
    if (!isLoading && !isAuthenticated) navigate('/admin/login', { replace: true });
  }, [isLoading, isAuthenticated, navigate]);

  // Start the catalogue's style list while the admin is on another page, so
  // opening Catalogue does not wait on it.
  useEffect(() => {
    if (isAuthenticated) prefetchStyles();
  }, [isAuthenticated]);

  useEffect(() => setScrolled(false), [path]);

  if (isLoading || !isAuthenticated) {
    return (
      <div className="oa-root flex min-h-screen items-center justify-center bg-[var(--oa-ground)] text-[var(--oa-muted)]">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );
  }

  const name = user?.name || user?.email?.split('@')[0] || 'Admin';
  // Phone tab bar: a short bar centred over the active tab's icon.
  const tabIndicator = tabs.rect ? { width: tabs.rect.width * 0.44, transform: `translateX(${tabs.rect.left + tabs.rect.width * 0.28}px)` } : { opacity: 0 };

  return (
    <ShellContext.Provider value={{ actionsEl, chatOnline: chat.online }}>
      <div className="oa-root relative flex h-screen flex-col bg-[var(--oa-ground)]">
        <Toaster position="bottom-right" richColors closeButton theme={mode} />
        <div className="oa-backdrop" aria-hidden="true">
          <i className="oa-blob oa-b1" />
          <i className="oa-blob oa-b2" />
          <i className="oa-blob oa-b3" />
        </div>

        {/* Above the phone tab bar, so the date sheet that lives in the header can cover it. */}
        <div className="relative z-50 shrink-0 px-3 pt-3 md:px-5 md:pt-4">
          <header className={cn('oa-top w-full', scrolled && 'is-scrolled')}>
            <Link to="/admin" className="flex shrink-0 items-center gap-2.5" aria-label="Outpost admin home">
              <OcMark />
              <span className="font-montserrat text-[16px] font-bold tracking-[-0.02em]">Outpost</span>
              <span className="oa-hide-sm rounded-md border border-[var(--oa-line-2)] px-1.5 py-px text-[10px] font-semibold uppercase tracking-[0.08em] text-[var(--oa-faint)]">Admin</span>
            </Link>

            <nav ref={nav.ref} className="oa-nav" aria-label="Admin">
              <span className="oa-ind" style={nav.style} aria-hidden="true" />
              {items.map((n) => (
                <NavLink key={n.to} to={n.to} end={n.end} aria-current={isActive(n) ? 'page' : undefined}>
                  {n.label}
                  {n.to === '/admin/livechat' && waitingChats > 0 ? (
                    <span className="grid h-[18px] min-w-[18px] place-items-center rounded-[5px] bg-[var(--oa-warn)] px-1 text-[10.5px] font-semibold tabular-nums text-[#1a1200]" aria-label={`${waitingChats} waiting`}>
                      {waitingChats}
                    </span>
                  ) : null}
                </NavLink>
              ))}
            </nav>

            <div className="oa-tools flex shrink-0 items-center gap-1">
              <div ref={setActionsEl} className="flex items-center" />
              <span className="oa-sep" aria-hidden="true" />
              <button
                type="button"
                onClick={chat.toggle}
                disabled={chat.online === null}
                title={chat.online ? 'Live chat: you are online. Click to go offline.' : 'Live chat: offline. Click to go online.'}
                className="oa-status-btn oa-hide-sm disabled:opacity-50"
              >
                <span className={cn('oa-pulse', chat.online && 'is-live')} style={{ ['--st' as string]: chat.online ? 'var(--oa-good)' : 'var(--oa-faint)' }} />
                {chat.online ? 'Chat online' : 'Chat offline'}
              </button>
              <a className="oa-icon-btn oa-hide-sm" href="/" target="_blank" rel="noreferrer" aria-label="View site" title="View site">
                <ExternalLink />
              </a>
              <button type="button" className="oa-icon-btn" onClick={toggleMode} aria-label={mode === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'} title={mode === 'dark' ? 'Light mode' : 'Dark mode'}>
                {mode === 'dark' ? <Sun /> : <Moon />}
              </button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button type="button" className="ml-1 rounded-[10px] outline-none" aria-label={`Account: ${name}`}>
                    <span className="oa-avatar">{initialsOf(name)}</span>
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" sideOffset={10} className="oa-menu">
                  <DropdownMenuLabel className="px-2 py-1.5 font-normal">
                    <span className="block truncate text-[13px] font-semibold text-[var(--oa-text)]">{name}</span>
                    <span className="block truncate text-[12px] text-[var(--oa-muted)]">{user?.email}</span>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator className="bg-[var(--oa-line-2)]" />
                  <DropdownMenuItem onSelect={chat.toggle} className="gap-2 sm:hidden">
                    <span className={cn('oa-pulse', chat.online && 'is-live')} style={{ ['--st' as string]: chat.online ? 'var(--oa-good)' : 'var(--oa-faint)' }} />
                    {chat.online ? 'Go offline for chat' : 'Go online for chat'}
                  </DropdownMenuItem>
                  {/* Pages outside the main nav. */}
                  <DropdownMenuItem onSelect={() => navigate('/admin/categories')} className="gap-2">
                    <FolderTree className="h-3.5 w-3.5" /> Categories
                  </DropdownMenuItem>
                  {isAdmin ? (
                    <DropdownMenuItem onSelect={() => navigate('/admin/users')} className="gap-2">
                      <Users className="h-3.5 w-3.5" /> Admin users
                    </DropdownMenuItem>
                  ) : null}
                  <DropdownMenuSeparator className="bg-[var(--oa-line-2)]" />
                  <DropdownMenuItem onSelect={() => window.open('/', '_blank')} className="gap-2">
                    <ExternalLink className="h-3.5 w-3.5" /> View site
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onSelect={() => {
                      signOut();
                      navigate('/admin/login', { replace: true });
                    }}
                    className="gap-2"
                  >
                    <LogOut className="h-3.5 w-3.5" /> Sign out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </header>
        </div>

        {!framed ? (
          <main className="relative z-10 min-h-0 flex-1 overflow-y-auto pb-[96px] md:pb-0" onScroll={(e) => setScrolled(e.currentTarget.scrollTop > 8)}>
            <Outlet />
          </main>
        ) : (
          <main className="relative z-10 min-h-0 flex-1 px-3 pb-[88px] pt-3 md:px-5 md:pb-5 md:pt-4">
            <div className="oa-frame h-full overflow-hidden rounded-[16px] border border-[color:var(--oa-line)] bg-[color:var(--oa-card)] shadow-[var(--oa-shadow)]">
              <div className="h-full overflow-auto">
                <Outlet />
              </div>
            </div>
          </main>
        )}

        <nav ref={tabs.ref} className="oa-tabbar" aria-label="Admin" style={{ gridTemplateColumns: `repeat(${items.length}, minmax(0, 1fr))` }}>
          <span className="oa-tind" style={tabIndicator} aria-hidden="true" />
          {items.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.end} aria-current={isActive(n) ? 'page' : undefined}>
              {n.to === '/admin/livechat' && waitingChats > 0 ? (
                <span className="absolute right-[24%] top-1 grid h-4 min-w-4 place-items-center rounded-[5px] bg-[var(--oa-warn)] px-1 text-[10px] font-semibold text-[#1a1200]">{waitingChats}</span>
              ) : null}
              <n.icon />
              {n.label}
            </NavLink>
          ))}
        </nav>
      </div>
    </ShellContext.Provider>
  );
};

export default AdminShell;
